/**
 * The image upload choreography (`062-images-assets.md` §3, `061-editor.md` §5).
 *
 * **The step order is load-bearing.** Written out once, because getting it wrong is silent
 * and permanent:
 *
 * ```
 * insert node (blob:) ──► upload ──► swap in real src + assetId ──► autosave
 *                                                                    ▲
 *                                             NOTHING may save before this point
 * ```
 *
 * A `blob:` URL is valid only inside the tab that created it. Persist one and the database
 * holds a permanently broken image: it renders fine until the tab closes, and then it is a
 * dead reference forever, with the bytes never having been uploaded to anything.
 *
 * Three mechanisms enforce it, and all three exist because any one of them can be defeated
 * by a code path nobody has written yet:
 *
 *  1. the `pendingUploads` row is written **before** the POST, and `useAutosave` refuses to
 *     enqueue while `hasPendingUploads(pageId)` is true;
 *  2. when the **last** upload settles, this file explicitly fires the deferred save — the
 *     half everyone forgets. Without it, a user who pastes an image and then stops typing
 *     would never save at all: the debounce was suppressed and nothing re-arms it;
 *  3. `stripTransientAttrs` (`lib/content.ts`) drops any node still holding a `blob:` src,
 *     and `outbox.enqueue` throws on one. Belt, braces, and a second pair of braces.
 */
import type { Editor } from '@tiptap/react';
import { toast } from '@/components/ui/sonner';
import {
  ALLOWED_MIME,
  MAX_UPLOAD_BYTES,
  uploadAsset,
  type AssetResponse,
} from '@/api/endpoints/assets';
import { ulid } from '@/lib/ulid';
import { translate } from '@/lib/i18n';
import type { PendingUpload } from '@/offline/db';
import {
  clearPendingUpload,
  listPendingUploads,
  markUploadFailed,
  putPendingUpload,
  requeueUpload,
} from '@/offline/uploads';

export interface UploadContext {
  pageId: string;
  /**
   * Fired when the last in-flight upload for this page settles — `062` §3 step 7, half 2.
   * The editor wires this to an immediate save.
   */
  onSettled: () => void;
}

/** `062` §3 step 3: a burst of ten pasted screenshots must not open ten sockets. */
const MAX_CONCURRENT = 3;

/* ── The in-flight ledger ───────────────────────────────────────────────────── */

const inflight = new Map<string, number>();

function enterFlight(pageId: string): void {
  inflight.set(pageId, (inflight.get(pageId) ?? 0) + 1);
}

function leaveFlight(ctx: UploadContext): void {
  const left = (inflight.get(ctx.pageId) ?? 1) - 1;

  if (left > 0) {
    inflight.set(ctx.pageId, left);
    return;
  }

  inflight.delete(ctx.pageId);
  // THE HALF EVERYONE FORGETS. The save was suppressed while this was in flight; nothing
  // else will ever re-arm it.
  ctx.onSettled();
}

/** A tiny semaphore. A real queue library would be three dependencies and an ADR. */
let active = 0;
const waiting: (() => void)[] = [];

async function acquire(): Promise<void> {
  if (active < MAX_CONCURRENT) {
    active += 1;
    return;
  }
  await new Promise<void>((resolve) => waiting.push(resolve));
  active += 1;
}

function release(): void {
  active -= 1;
  waiting.shift()?.();
}

/* ── Node surgery ───────────────────────────────────────────────────────────── */

/**
 * Decode the intrinsic size BEFORE inserting, so the node reserves the right box and the
 * text below it does not jump when the real URL swaps in. Layout shift is the tell that an
 * app is faking it, and this is the whole reason we bother.
 */
async function decodeDimensions(
  objectUrl: string,
): Promise<{ width: number; height: number } | null> {
  try {
    const image = new Image();
    image.src = objectUrl;
    await image.decode();
    if (image.naturalWidth === 0) return null;
    return { width: image.naturalWidth, height: image.naturalHeight };
  } catch {
    return null; // a corrupt file. The caller toasts and does not upload.
  }
}

/**
 * The swap (`062` §3 step 6). Three details, each of which is a bug if missed:
 *
 *  - **Found by `uploadId`, never by position.** The user keeps typing while the upload is
 *    in flight, so the node has moved. A position taken at paste time is stale the moment
 *    it is taken.
 *  - **`addToHistory: false`.** If the swap enters the undo stack, `Cmd+Z` after a paste
 *    puts the (possibly already revoked) `blob:` URL back — and that document can then be
 *    saved. The swap is a system transaction, not a user edit.
 *  - **`revokeObjectURL`.** Object URLs live until the document unloads. Fifty screenshots
 *    in a session is fifty images held in memory.
 */
export function resolveUpload(
  editor: Editor,
  uploadId: string,
  asset: AssetResponse,
  objectUrl: string | null,
): boolean {
  const { state, view } = editor;
  const tr = state.tr;
  let found = false;

  state.doc.descendants((node, pos) => {
    if (found) return false;
    if (node.type.name !== 'image' || node.attrs['uploadId'] !== uploadId) return true;

    tr.setNodeMarkup(pos, undefined, {
      ...node.attrs,
      src: asset.url,
      assetId: asset.id, // REQUIRED. Without it GC cannot see the reference (`031`).
      width: asset.width,
      height: asset.height,
      uploading: false,
      failed: false,
      uploadId: null,
    });
    found = true;
    return false;
  });

  if (found) {
    tr.setMeta('addToHistory', false);
    view.dispatch(tr);
  }

  // If the node is GONE (undo, or the user deleted the image mid-upload): revoke and do
  // nothing else. The asset row exists on the server, unreferenced — and GC's 24-hour grace
  // period exists for exactly this case. Do not delete it eagerly; the user may hit redo.
  if (objectUrl !== null) URL.revokeObjectURL(objectUrl);
  return found;
}

/** The upload failed. The node stays, the blob stays, and a Retry chip appears. */
function markNodeFailed(editor: Editor, uploadId: string): void {
  const { state, view } = editor;
  const tr = state.tr;
  let found = false;

  state.doc.descendants((node, pos) => {
    if (found) return false;
    if (node.type.name !== 'image' || node.attrs['uploadId'] !== uploadId) return true;

    // `src` stays a `blob:` URL so the user can still SEE their image. It never reaches the
    // server: `stripTransientAttrs` drops the node from the save payload, and the blob lives
    // on in IndexedDB for the retry. The words save; the picture waits.
    tr.setNodeMarkup(pos, undefined, { ...node.attrs, uploading: false, failed: true });
    found = true;
    return false;
  });

  if (found) {
    tr.setMeta('addToHistory', false);
    view.dispatch(tr);
  }
}

/* ── The flow ───────────────────────────────────────────────────────────────── */

function validate(file: File): string | null {
  if (file.size > MAX_UPLOAD_BYTES) {
    return translate('editor.upload.tooLarge', { name: file.name });
  }
  // A soft check only: `file.type` comes from the OS and is not trustworthy. The SERVER
  // sniffs the bytes (`062` §4.1). A file the client cannot identify is still uploaded and
  // the server gets to decide.
  if (file.type !== '' && !(ALLOWED_MIME as readonly string[]).includes(file.type)) {
    return translate('editor.upload.unsupportedType', { name: file.name });
  }
  return null;
}

/**
 * Step 2 — insert the node IMMEDIATELY, with a blob URL, before a single byte goes over the
 * wire. The image is on screen NOW. Perceived latency: zero. This is the point of the whole
 * design (`140-roadmap.md` M3 acceptance #1: on screen in under 100 ms).
 */
async function insertPendingImage(
  editor: Editor,
  file: File,
  pos: number | 'end' | null,
): Promise<{ uploadId: string; objectUrl: string; row: PendingUpload } | null> {
  const objectUrl = URL.createObjectURL(file);
  const size = await decodeDimensions(objectUrl);

  if (size === null) {
    URL.revokeObjectURL(objectUrl);
    toast.error(translate('editor.upload.unreadable', { name: file.name }));
    return null;
  }

  const uploadId = ulid();
  const at = pos === 'end' ? editor.state.doc.content.size : (pos ?? editor.state.selection.from);

  editor
    .chain()
    .insertContentAt(at, {
      type: 'image',
      attrs: {
        src: objectUrl,
        alt: file.name.replace(/\.[^.]+$/, ''),
        assetId: null,
        width: size.width,
        height: size.height,
        uploading: true,
        uploadId,
      },
    })
    .run();

  return {
    uploadId,
    objectUrl,
    row: {
      clientId: uploadId,
      pageId: '',
      blob: file,
      filename: file.name,
      mimeType: file.type,
      width: size.width,
      height: size.height,
      status: 'uploading',
      attempts: 0,
      nextAttemptAt: Date.now(),
      createdAt: Date.now(),
    },
  };
}

async function send(
  editor: Editor,
  ctx: UploadContext,
  file: File,
  uploadId: string,
  objectUrl: string | null,
): Promise<void> {
  await acquire();
  try {
    const asset = await uploadAsset(file, ctx.pageId);

    // Step 6 THEN step 7, in that order and never the other way round: the node must carry
    // the real src before the row that gates the save disappears.
    resolveUpload(editor, uploadId, asset, objectUrl);
    await clearPendingUpload(uploadId);
  } catch (error) {
    // The bytes are already in IndexedDB. Nothing is lost — it is queued.
    await markUploadFailed(uploadId, error instanceof Error ? error.message : 'Upload failed');
    markNodeFailed(editor, uploadId);
    toast.error(translate('editor.upload.failed'), {
      description: translate('editor.upload.failedDescription'),
    });
  } finally {
    release();
    leaveFlight(ctx);
  }
}

/** Insert `files` at a position (or at the selection, or appended) and upload each once. */
export async function insertImages(
  editor: Editor,
  ctx: UploadContext,
  files: File[],
  pos: number | 'end' | null,
): Promise<void> {
  for (const file of files) {
    const problem = validate(file);
    if (problem !== null) {
      toast.error(problem);
      continue;
    }

    const pending = await insertPendingImage(editor, file, pos);
    if (pending === null) continue;

    // BEFORE the POST. If the tab dies during the request, the bytes survive.
    await putPendingUpload({ ...pending.row, pageId: ctx.pageId });
    enterFlight(ctx.pageId);

    void send(editor, ctx, file, pending.uploadId, pending.objectUrl);
  }
}

/* ── The double-insert trap (`061` §5) ──────────────────────────────────────── */

/**
 * **The single detail that separates a working image paste from a maddening one.**
 *
 * A Word paste with an inline image puts BOTH an HTML fragment (with an `<img>`) AND the
 * image's bytes on the clipboard. Handle the files here *and* let ProseMirror parse the
 * HTML, and the image is inserted twice.
 *
 * But our sanitizer *removes* `file:///` and `cid:` `<img>` elements (`061` §4.4) — so in
 * the Word case the HTML contributes no image node at all, and the blob is the only chance
 * we have of keeping the picture. Hence the rule, which is a pure function so it can be
 * tested without a browser:
 *
 *  - the sanitized HTML still contains an `<img>` → **the HTML wins entirely; upload nothing.**
 *    (Uploading too would duplicate it.)
 *  - the sanitized HTML contains NO `<img>` but blobs are present → those blobs *were* the
 *    document's images, and we dropped their unfetchable srcs. Upload them, and append.
 */
export function shouldUploadPastedFiles(sanitizedHtml: string, fileCount: number): boolean {
  if (fileCount === 0) return false;
  return !/<img\b/i.test(sanitizedHtml);
}

/* ── Retry, and life after a reload (`062` §5) ──────────────────────────────── */

export async function retryUpload(
  editor: Editor,
  ctx: UploadContext,
  clientId: string,
): Promise<void> {
  const row = await requeueUpload(clientId);
  if (row === undefined) return;

  const file = new File([row.blob], row.filename, { type: row.mimeType });
  enterFlight(ctx.pageId);
  await send(editor, ctx, file, clientId, null);
}

/**
 * App boot / editor mount. For every stored blob whose node is still in the document, hand
 * the node a fresh object URL from IndexedDB.
 *
 * **The user sees their image again after a full browser restart, even though it never
 * reached the server.** That is the payoff for storing the blob rather than an error string.
 * Failed rows are re-rendered as failed; pending ones are retried straight away.
 */
export async function restorePendingUploads(editor: Editor, ctx: UploadContext): Promise<void> {
  const rows = await listPendingUploads(ctx.pageId);
  if (rows.length === 0) return;

  const byId = new Map(rows.map((row) => [row.clientId, row]));
  const matched = new Set<string>();

  const { state, view } = editor;
  const tr = state.tr;

  state.doc.descendants((node, pos) => {
    if (node.type.name !== 'image') return true;

    const uploadId = node.attrs['uploadId'];
    if (typeof uploadId !== 'string') return true;

    const row = byId.get(uploadId);
    if (row === undefined) return true;

    matched.add(uploadId);
    tr.setNodeMarkup(pos, undefined, {
      ...node.attrs,
      src: URL.createObjectURL(row.blob), // the image is back on screen, from local bytes
      uploading: true,
      failed: false,
    });
    return true;
  });

  if (matched.size > 0) {
    tr.setMeta('addToHistory', false);
    view.dispatch(tr);
  }

  for (const row of rows) {
    if (matched.has(row.clientId)) {
      void retryUpload(editor, ctx, row.clientId);
      continue;
    }

    // The node is gone: undone, or deleted while offline. The bytes are NOT thrown away
    // (only a human may do that — `062` §5.7), but the row must stop gating this page's
    // save forever, so it is parked as `failed`. The server-side orphan is GC's problem,
    // and its 24-hour grace period exists for exactly this case.
    if (row.status !== 'failed') await markUploadFailed(row.clientId, 'The image was removed.');
  }
}
